/**
 * Service for expense split calculations and validations
 */
import {
  ExpenseSplitter,
  SplitMethod,
  SplitOptions,
  formatAmountWithSeparators,
  getCurrencyDecimals,
  getCurrencyLocale,
  roundToCurrencyPrecision,
  sumsMatchInMinorUnits,
} from '@splitfiesta/shared';

export interface Split {
  userId: string;
  userName: string;
  amount?: number;
  amountFormatted?: string;
  percentage?: number;
  shares?: number;
  defaultShares?: number;
  included: boolean;
}

export interface Payer {
  userId: string;
  amount: number;
}

export interface SplitValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface PayerValidationResult {
  isValid: boolean;
  error?: string;
}

class SplitCalculationService {
  /**
   * Calculate display amounts for splits based on method
   */
  calculateSplitDisplayAmounts(
    totalAmount: number,
    currency: string,
    splitMethod: SplitMethod,
    splits: Split[],
    options?: SplitOptions
  ): { [userId: string]: number } {
    const displayAmounts: { [userId: string]: number } = {};

    if (totalAmount <= 0) return displayAmounts;
    if (!currency) return displayAmounts;

    const includedSplits = splits.filter(s => s.included);
    if (includedSplits.length === 0) return displayAmounts;

    try {
      const calculatedSplits = ExpenseSplitter.splitExpense(
        totalAmount,
        currency,
        splitMethod,
        includedSplits.map(s => ({
          userId: s.userId,
          amount: s.amount,
          percentage: s.percentage,
          shares: s.shares ?? s.defaultShares,
        })),
        options
      );

      calculatedSplits.forEach(split => {
        displayAmounts[split.userId] = split.amount;
      });
    } catch {
      // Error calculating split amounts, return empty
    }

    return displayAmounts;
  }

  /**
   * Validate splits based on method
   */
  validateSplits(
    totalAmount: number,
    splitMethod: SplitMethod,
    splits: Split[],
    currency: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): SplitValidationResult {
    const includedSplits = splits.filter(s => s.included);

    if (includedSplits.length === 0) {
      return {
        isValid: false,
        errors: [t('expenses:errors.atLeastOnePersonRequired')],
      };
    }

    const validation = ExpenseSplitter.validateSplit(
      totalAmount,
      currency,
      splitMethod,
      includedSplits.map(s => ({
        userId: s.userId,
        amount: s.amount,
        percentage: s.percentage,
      }))
    );

    if (!validation.isValid) {
      // Translate error keys
      const translatedErrors = validation.errors.map(error => {
        // Check if it's a translation key (format: namespace:key)
        if (error.includes(':')) {
          // Remove the namespace prefix
          const key = error.split(':').slice(1).join(':');
          return t(`expenses:${key}`);
        }
        return error;
      });

      return {
        isValid: false,
        errors: translatedErrors,
      };
    }

    return { isValid: true, errors: [] };
  }

  /**
   * Validate multi-payer amounts
   */
  validatePayers(
    payers: Payer[],
    expectedAmount: number,
    currency: string,
    t: (key: string, params?: Record<string, unknown>) => string
  ): PayerValidationResult {
    if (!Array.isArray(payers) || payers.length === 0) {
      return {
        isValid: false,
        error: t('expenses:errors.atLeastOnePayerRequired'),
      };
    }

    const totalPaid = payers.reduce(
      (sum, payer) => sum + (payer.amount || 0),
      0
    );
    const roundedExpected = roundToCurrencyPrecision(expectedAmount, currency);

    if (
      !sumsMatchInMinorUnits(
        payers.map(payer => payer.amount || 0),
        expectedAmount,
        currency
      )
    ) {
      const formattedTotal = formatAmountWithSeparators(
        totalPaid,
        currency,
        getCurrencyLocale(currency)
      );
      const formattedExpected = formatAmountWithSeparators(
        roundedExpected,
        currency,
        getCurrencyLocale(currency)
      );

      return {
        isValid: false,
        error: t('expenses:errors.payerAmountMismatch', {
          total: formattedTotal,
          expected: formattedExpected,
        }),
      };
    }

    const emptyPayers = payers.filter(p => !p.amount || p.amount === 0);
    if (emptyPayers.length > 0) {
      return {
        isValid: false,
        error: t('expenses:errors.emptyPayerAmounts'),
      };
    }

    return { isValid: true };
  }

  /**
   * Format amount with currency-specific decimal places
   */
  formatAmountForCurrency(amount: number, currency: string): string {
    return amount.toFixed(getCurrencyDecimals(currency));
  }

  /**
   * Create final split data for API submission
   */
  createFinalSplitData(
    splits: Split[],
    splitMethod: SplitMethod,
    displayAmounts: { [userId: string]: number },
    currency: string
  ) {
    const includedSplits = splits.filter(s => s.included);

    return includedSplits.map(s => {
      const splitData: {
        userId: string;
        amount: number;
        percentage?: number;
        shares?: number;
      } = {
        userId: s.userId,
        amount:
          splitMethod === 'custom'
            ? roundToCurrencyPrecision(s.amount || 0, currency)
            : roundToCurrencyPrecision(
                displayAmounts[s.userId] || s.amount || 0,
                currency
              ),
      };

      // Only include percentage if it's greater than 0 (backend validation requires percentage > 0)
      if (s.percentage && s.percentage > 0) {
        splitData.percentage = s.percentage;
      }

      // Include shares when split method is 'shares'
      if (splitMethod === 'shares') {
        splitData.shares = s.shares ?? s.defaultShares ?? 1;
      }

      return splitData;
    });
  }

  /**
   * Create final payer data for API submission
   */
  createFinalPayerData(payers: Payer[]): Payer[] {
    return payers.filter(p => p.amount > 0);
  }

  /**
   * Check if payers are in default state (single payer with 0 amount)
   */
  hasDefaultPayer(payers: Payer[], userId: string): boolean {
    return (
      payers.length === 1 &&
      payers[0].amount === 0 &&
      payers[0].userId === userId
    );
  }

  /**
   * Round amount to currency precision
   */
  roundAmount(amount: number, currency: string): number {
    return roundToCurrencyPrecision(amount, currency);
  }
}

export const splitCalculationService = new SplitCalculationService();
